<template>
  <div class="">
    <HeroSection />

    <!-- Mobile Filter and Actions -->
    <MobileFilterActions
      v-if="isMobile"
      :search-query="searchQuery"
      :selected-sort="
        activeTab === 'custom' ? selectedSort : signatureSelectedSort
      "
      :selected-locale="activeLocale"
      :is-sticky="isMobileFilterSticky"
      :active-tab="activeTab"
      @update:search-query="
        activeTab === 'custom'
          ? (searchQuery = $event)
          : (signatureSearchQuery = $event)
      "
      @update:selected-sort="
        activeTab === 'custom'
          ? (selectedSort = $event)
          : (signatureSelectedSort = $event)
      "
      @update:selected-locale="activeLocale = $event"
      @search="handleSearch"
      @clear-search="clearSearch"
      @filter-clicked="handleMobileFilterClick"
      @sort-changed="
        activeTab === 'custom'
          ? sortChanged($event)
          : signatureSortChanged($event)
      "
      @locale-changed="localeChanged"
    />

    <div class="content-container-maker-cart container-xxl">
      <div class="" :style="sidebarStyle">
        <FilterComponent
          v-if="!isMobile"
          :selectedFilters="selectedFilters"
          :initialFilters="initialFilters"
          :locale="activeLocale"
          :disabled="activeTab === 'signature' || activeTab === 'makercrate'"
          @filtersChanged="filtersChanged"
          @modeChanged="onFilterModeChanged"
        />
      </div>
      <div class="flex-grow-1" :style="mainContentStyle">
        <TabsSection :active-tab="activeTab" @tab-changed="handleTabChange" />

        <HeaderCreateSection
          :active-tab="activeTab"
          :has-cart-with-activities="hasCartWithActivities"
          :displayed-carts="displayedCarts"
          :single-activities="singleActivities"
          :signature-makercarts="signatureMakercarts"
          :remaining-carts-count="remainingCartsCount"
        />

        <!-- Custom Makercart Tab Content -->
        <CustomMakercartSection
          v-if="activeTab === 'custom'"
          v-model:search-query="searchQuery"
          v-model:selected-sort="selectedSort"
          :active-locale="activeLocale"
          :selected-chips="selectedChips"
          :items="items"
          :is-loading="isLoading"
          :error="error"
          :error-code="errorCode"
          :is-user-logged="isUserLogged"
          :empty-state-title="emptyStateTitle"
          :not-found-message="notFoundMessage"
          :current-page="currentPage"
          :total-pages="totalPages"
          :visible-pages="visiblePages"
          @search="handleSearch"
          @clear-search="clearSearch"
          @locale-changed="localeChanged"
          @cart-clicked="updateVisibilityModalUserCart(true)"
          @clear-filter="clearFilter"
          @clear-all-filters="clearAllFilters"
          @sort-changed="sortChanged"
          @update-liked-status="updateLikedStatus"
          @item-clicked="handleItemClicked"
          @page-changed="changePage"
        />

        <!-- Catalog Makercart Tab Content -->
        <SignatureMakercartSection
          v-if="activeTab === 'signature'"
          :search-query="signatureSearchQuery"
          :selected-sort="signatureSelectedSort"
          :active-locale="activeLocale"
          :is-user-logged="isUserLogged"
          @update:search-query="signatureSearchQuery = $event"
          @update:selected-sort="signatureSelectedSort = $event"
          @update:selected-locale="activeLocale = $event"
          @search="handleSearch"
          @clear-search="clearSearch"
          @sort-changed="signatureSortChanged"
          @locale-changed="localeChanged"
          @modal-state-changed="handleSignatureModalStateChanged"
        />

        <!-- Makercrate Tab Content -->
        <MakercrateSection
          v-if="activeTab === 'makercrate'"
          :active-locale="activeLocale"
          @update:selected-locale="activeLocale = $event"
          @locale-changed="localeChanged"
        />
      </div>
    </div>

    <MakerCartDetail
      ref="makerCartDetail"
      @modal-closed="handleModalClosed"
      @view-limit-reached="handleViewLimitReached"
    ></MakerCartDetail>
    <CustomMakercartDetailModal
      ref="makercartDetailModal"
      :visible="showMakercartDetailModal"
      :makercart-id="selectedMakercartId"
      @close="handleMakercartDetailModalClose"
      @add-to-cart="handleMakercartModalAddToCart"
    />
    <UserCart
      ref="userCart"
      @update:confirmation="updateVisibilityModalConfirmation"
      @refreshCart="() => {}"
      @triggerUpdateVisibilityModalSuccess="updateVisibilityModalSuccess"
      @triggerUpdateVisibilityModalConfirmation="
        updateVisibilityModalConfirmation
      "
    ></UserCart>
    <ConfirmationModal
      ref="confirmationModal"
      @confirm="handleConfirm"
      @cancel="handleCancel"
    ></ConfirmationModal>
    <SuccessModal ref="successModal"></SuccessModal>
    <LoginRequiredModal
      v-model="showLoginRequiredModal"
      title="Sign in/sign in to continue your order"
      description="Please sign in or sign up to request your Quote."
      :redirect-path="computedRedirectPath"
      @close="handleLoginModalClose"
    />
    <MinimumRequestModal ref="minimumRequestModal"></MinimumRequestModal>
    <ViewLimitModal
      v-model="showViewLimitModal"
      :limit-resource-left="limitResourceLeft"
      @user-authenticated="handleUserAuthenticated"
    />
    <ChatbotAssistant
      v-if="!isAnyModalOpen"
      position="right"
      page-name="Catalog"
    />
    <MobileFilterBottomSheet
      v-if="isMobile"
      v-model:visible="showMobileFilterSheet"
      :selectedFilters="selectedFilters"
      :initialFilters="initialFilters"
      :locale="activeLocale"
      @filtersChanged="handleMobileFiltersChanged"
      @close="handleMobileFilterClose"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import ConfirmationModal from "@/components/maker-cart/ConfirmationModal.vue";
import MakerCartDetail from "@/components/maker-cart/MakerCartDetail.vue";
import CustomMakercartDetailModal from "@/components/maker-cart/catalog/custom/CustomMakercartDetailModal.vue";
import MinimumRequestModal from "@/components/maker-cart/MinimumRequestModal.vue";
import SuccessModal from "@/components/maker-cart/SuccessModal.vue";
import UserCart from "@/components/maker-cart/UserCart.vue";
import ViewLimitModal from "@/components/maker-cart/ViewLimitModal.vue";
import LoginRequiredModal from "@/components/modal/LoginRequiredModal.vue";
import FilterComponent from "@/views/MakerCart/FilterComponent.vue";
import HeroSection from "@/components/maker-cart/catalog/HeroSection.vue";
import TabsSection from "@/components/maker-cart/catalog/TabsSection.vue";
import HeaderCreateSection from "@/components/maker-cart/catalog/HeaderCreateSection.vue";
import CustomMakercartSection from "@/components/maker-cart/catalog/custom/CustomMakercartSection.vue";
import SignatureMakercartSection from "@/components/maker-cart/catalog/signature/SignatureMakercartSection.vue";
import MakercrateSection from "@/components/maker-cart/catalog/makercrate/MakercrateSection.vue";
import MobileFilterActions from "@/components/maker-cart/catalog/MobileFilterActions.vue";
import MobileFilterBottomSheet from "@/components/maker-cart/catalog/MobileFilterBottomSheet.vue";
import { useMakerCartCatalog } from "@/components/maker-cart/catalog/useMakerCartCatalog.js";
import { useMakerCartModals } from "@/components/maker-cart/catalog/useMakerCartModals.js";
import { onBeforeUnmount, onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useCart } from "@/composeables/useCart";
import ChatbotAssistant from "@/components/maker-cart/ChatbotAssistant.vue";

const route = useRoute();

// Use composables
const {
  // State
  searchQuery,
  selectedSort,
  activeLocale,
  items,
  isLoading,
  error,
  errorCode,
  currentPage,
  totalPages,
  activeTab,
  initialFilters,

  // Computed
  visiblePages,
  selectedChips,
  emptyStateTitle,
  notFoundMessage,
  sidebarStyle,
  mainContentStyle,

  // Methods
  handleSearch,
  clearSearch,
  changePage,
  sortChanged,
  localeChanged,
  clearFilter,
  clearAllFilters,
  filtersChanged,
  onFilterModeChanged,
  setActiveTab,
  handleResize,
  parseFiltersFromUrl,
} = useMakerCartCatalog();

const {
  // Refs
  makerCartDetail,
  userCart,
  confirmationModal,
  successModal,
  minimumRequestModal,
  makercartDetailModal,

  // State
  showLoginRequiredModal,
  showViewLimitModal,
  showMakercartDetailModal,
  selectedMakercartId,
  limitResourceLeft,
  isUserLogged,

  // Computed
  hasCartWithActivities,
  displayedCarts,
  remainingCartsCount,
  computedRedirectPath,
  signatureMakercarts,
  // Methods
  updateVisibilityModalMakerCartDetail,
  handleModalClosed,
  updateVisibilityMakercartDetailModal,
  handleMakercartDetailModalClose,
  handleMakercartModalAddToCart,
  handleItemClicked,
  updateVisibilityModalUserCart,
  updateVisibilityModalConfirmation,
  updateVisibilityModalSuccess,
  handleLoginModalClose,
  handleUserAuthenticated,
  handleConfirm,
  handleCancel,
  handleViewLimitReached,
  checkAuthToken,
  updateLikedStatus,
} = useMakerCartModals();

// Additional state
const selectedFilters = ref([]);
const { singleActivities, syncCartWithAPI } = useCart();

// Signature tab state (separate from custom tab)
const signatureSearchQuery = ref("");
const signatureSelectedSort = ref("newest");
const isSignatureModalOpen = ref(false);

// Computed to track if any modal is open
const isAnyModalOpen = computed(() => {
  return (
    showMakercartDetailModal.value ||
    showLoginRequiredModal.value ||
    showViewLimitModal.value ||
    isSignatureModalOpen.value ||
    showMobileFilterSheet.value
  );
});

// Mobile-specific state
const isMobile = computed(() => window.innerWidth <= 768);
const isMobileFilterSticky = ref(false);
const showMobileFilterSheet = ref(false);

// Mobile-specific methods
const handleMobileFilterClick = () => {
  showMobileFilterSheet.value = true;
};

const handleMobileFilterClose = () => {
  showMobileFilterSheet.value = false;
};

const handleMobileFiltersChanged = (filters) => {
  // Handle the filter changes from mobile filter sheet
  filtersChanged(filters);
};

const signatureSortChanged = (value) => {
  signatureSelectedSort.value = value;
  console.log("Signature sort changed:", value);
};

const handleSignatureModalStateChanged = (isOpen) => {
  isSignatureModalOpen.value = isOpen;
};

// Wrapper for setActiveTab to also reset signature tab state
const handleTabChange = (tab) => {
  // Reset signature tab state when switching tabs
  signatureSearchQuery.value = "";
  signatureSelectedSort.value = "newest";

  // Call the original setActiveTab from composable
  setActiveTab(tab);
};

const handleScroll = () => {
  if (!isMobile.value) return;

  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const heroHeight = 210; // Approximate height of hero section + tabs

  isMobileFilterSticky.value = scrollTop > heroHeight;
};

// Initialize auth checking
let intervalId;

onMounted(async () => {
  // Add window resize listener for responsive sidebar
  window.addEventListener("resize", handleResize);

  // Add scroll listener for mobile sticky filter
  window.addEventListener("scroll", handleScroll);

  // Keep cart badge in sync with API (includes makercrates)
  syncCartWithAPI();

  // Parse URL parameters first (this sets activeTab based on URL)
  parseFiltersFromUrl();

  // Check for query parameter to open modal
  if (route.query.item && activeTab.value === "custom") {
    const useNewModal = true;
    if (useNewModal) {
      updateVisibilityMakercartDetailModal(true, route.query.item);
    } else {
      updateVisibilityModalMakerCartDetail(true, route.query.item);
    }
  }

  // Start auth checking after component is mounted
  checkAuthToken();
  intervalId = setInterval(checkAuthToken, 500);
});

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
  // Clean up window resize listener
  window.removeEventListener("resize", handleResize);
  // Clean up scroll listener
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
/* Smooth transitions for filter mode changes */
.content-container-maker-cart > div:first-child {
  transition: width 0.3s ease-in-out;
}

.content-container-maker-cart > div:last-child {
  transition: all 0.3s ease-in-out;
}

.content-container-maker-cart {
  width: 100%;
  margin: 0 auto;
  padding: 20px 40px;
  display: flex;
  position: relative;
}

@media (max-width: 768px) {
  .content-container-maker-cart {
    flex-direction: column;
    padding: 8px;
    gap: 16px;
  }

  .content-container-maker-cart > div:first-child {
    width: 100% !important;
    order: 2;
  }

  .content-container-maker-cart > div:last-child {
    width: 100% !important;
    order: 1;
    padding: 0 !important;
  }

  /* Add top padding when mobile filter is sticky */
  body:has(.mobile-filter-actions.is-sticky) {
    padding-top: 120px;
  }

  /* Ensure proper spacing for mobile layout */
  .content-container-maker-cart {
    margin-top: 0;
  }
}
</style>
